import {
  SCORE_BAR_MAX,
  SCORE_BAR_MIN,
  type CompletionRatio,
  type Habit,
  type HabitId,
  type HabitLog,
  type IsoDate,
} from '../models/types';

/**
 * Skorlama motoru — CLAUDE.md §7.2 ve §7.3'ün koda dökülmüş hâli.
 *
 * Bu dosyadaki her fonksiyon **saftır** (pure): aynı girdiye her zaman
 * aynı çıktıyı verir, veritabanına/DOM'a dokunmaz, tarih okumaz.
 * Sebebi şu — puanlama bu uygulamanın kalbi; saf tutulduğunda hem
 * tek başına doğrulanabilir (bkz. `scoring.verify.ts`), hem de bir
 * ekran değişikliği yanlışlıkla hesabı bozamaz.
 *
 * Veritabanına yazan gün-kapanışı mantığı ayrı dosyada:
 * `services/dailyRollover.ts`.
 */

/* ==================================================================
   Yardımcılar
   ================================================================== */

/**
 * Günün kayıtlarını `habitId -> oran` sözlüğüne çevirir.
 * Kaydı olmayan alışkanlık "hiç yapılmadı" (0) sayılır.
 */
function buildRatioMap(dayLogs: HabitLog[]): Map<HabitId, CompletionRatio> {
  const map = new Map<HabitId, CompletionRatio>();
  for (const log of dayLogs) {
    map.set(log.habitId, log.completionRatio);
  }
  return map;
}

/**
 * Belirli bir günde hesaba KATILAN alışkanlıkları döndürür.
 *
 * İki eleme yapılır:
 * 1. Arşivlenmiş (silinmiş) alışkanlıklar hiç sayılmaz.
 * 2. O günden SONRA oluşturulmuş alışkanlıklar sayılmaz — kullanıcı
 *    daha var olmayan bir alışkanlığı yapmadığı için cezalandırılamaz.
 *    (Bu kural §7'de açıkça yazmıyor ama tersi mantıksız olurdu:
 *    bugün yeni bir alışkanlık eklemek, geçmiş günleri geriye dönük
 *    olarak "başarısız" hâle getirirdi.)
 */
export function getScorableHabits(allHabits: Habit[], date: IsoDate): Habit[] {
  return allHabits.filter(
    (habit) => !habit.archived && habit.createdAt <= date,
  );
}

/* ==================================================================
   1) Tek bir alışkanlığın katkısı ve cezası (§7.2)
   ================================================================== */

/**
 * Alışkanlık Katkısı = Başarı Puanı × Katsayı × Bölünme Oranı
 *
 * Yapılan bir alışkanlığın günlük toplama eklediği POZİTİF değer.
 * Oran 0 ise sonuç da 0'dır (katkı yok) — ceza ayrı hesaplanır.
 *
 * Örnek: Puan 4, Katsayı 1.5, Oran %75 → 4 × 1.5 × 0.75 = 4.5
 */
export function calculateHabitContribution(
  habit: Habit,
  completionRatio: CompletionRatio,
): number {
  return habit.successScore * habit.coefficient * completionRatio;
}

/**
 * Alışkanlık Cezası = −(Başarı Puanı × Katsayı) / 2
 *
 * Yapılmayan bir alışkanlığın günlük toplamdan düşürdüğü değer.
 * NEGATİF bir sayı döner, böylece çağıran taraf çıkarma yapmak yerine
 * doğrudan toplayabilir (işaret hatası riski ortadan kalkar).
 *
 * Cezanın katkının yarısı olması bilinçlidir: bir günü kaçırmak, onu
 * yapmanın kazandırdığından daha az kaybettirir.
 *
 * Örnek: Puan 4, Katsayı 1.5 → −(4 × 1.5) / 2 = −3
 */
export function calculateHabitPenalty(habit: Habit): number {
  return -(habit.successScore * habit.coefficient) / 2;
}

/* ==================================================================
   2) Günlük ham toplam (§7.2)
   ================================================================== */

export interface DailyDeltaOptions {
  /** Hesabın yapıldığı gün — hangi alışkanlıkların sayılacağını belirler. */
  date: IsoDate;
  /**
   * Gün "dondurulmuş" mu? — tatil hakkı (§7.7) VEYA ödülle satın alma
   * (§7.6) yoluyla.
   *
   * İsim bilinçli olarak `isVacationDay` değil: iki farklı kaynak aynı
   * etkiyi yapıyor ve hesap motorunun hangisi olduğunu bilmesi
   * gerekmiyor. Adı tatile bağlı kalsaydı, ödül günü için `true`
   * geçmek okuyan kişiyi yanıltırdı.
   *
   * Dondurulmuş gün ASİMETRİKTİR: cezalar tamamen atlanır, ama fiilen
   * yapılan alışkanlıkların katkısı normal şekilde işler.
   */
  isFrozenDay: boolean;
}

/**
 * Günlük Ham Toplam = Σ(yapılanların katkısı) + Σ(yapılmayanların cezası)
 *
 * Bu, günün KAPANIŞ hesabıdır: yapılmamış alışkanlıkların cezası da
 * dahildir. Gün içindeki canlı gösterim için `calculateLiveGrossDelta`
 * kullanılır (o ceza uygulamaz).
 *
 * Dönen değer ondalıklı olabilir; tam sayıya yuvarlama ayrı bir adımdır
 * (`finalizeDailyDelta`) çünkü §7.2'ye göre yuvarlama tek tek katkılara
 * değil, günün NET toplamına uygulanır.
 */
export function calculateDailyGrossDelta(
  dayLogs: HabitLog[],
  allHabits: Habit[],
  options: DailyDeltaOptions,
): number {
  const habits = getScorableHabits(allHabits, options.date);
  const ratios = buildRatioMap(dayLogs);

  let total = 0;
  for (const habit of habits) {
    // Kaydı hiç yoksa alışkanlık o gün yapılmamış demektir.
    const ratio = ratios.get(habit.id) ?? 0;

    if (ratio > 0) {
      total += calculateHabitContribution(habit, ratio);
    } else if (!options.isFrozenDay) {
      // Dondurulmuş günde (tatil §7.7 veya ödül §7.6) ceza adımı
      // tamamen atlanır.
      total += calculateHabitPenalty(habit);
    }
  }

  return total;
}

/**
 * Gün İÇİNDEKİ canlı ham toplam — yalnızca yapılanların katkısı.
 *
 * §7.2'ye göre yapılmamış alışkanlıkların cezası gün kapanana kadar
 * uygulanmaz. Aksi hâlde kullanıcı sabah uygulamayı açtığında, henüz
 * hiçbir şey yapmadığı için barı dibe vurmuş görürdü.
 */
export function calculateLiveGrossDelta(
  dayLogs: HabitLog[],
  allHabits: Habit[],
  date: IsoDate,
): number {
  const habits = getScorableHabits(allHabits, date);
  const ratios = buildRatioMap(dayLogs);

  let total = 0;
  for (const habit of habits) {
    const ratio = ratios.get(habit.id) ?? 0;
    if (ratio > 0) {
      total += calculateHabitContribution(habit, ratio);
    }
  }

  return total;
}

/* ==================================================================
   3) Yuvarlama ve bara uygulama (§7.2)
   ================================================================== */

/**
 * Günün ham toplamını tam sayıya çevirir — her zaman **sıfıra doğru**.
 *
 *   Pozitifse aşağı:  18.7  → 18   (kazanç azalır)
 *   Negatifse yukarı: −3.2  → −3   (ceza azalır)
 *
 * Yani yuvarlama HER ZAMAN kullanıcı lehinedir. Bu yüzden
 * `Math.floor()` DEĞİL, `Math.trunc()` kullanılır — `Math.floor(-3.2)`
 * −4 verirdi ve cezayı artırırdı.
 *
 * Yuvarlamadan önce 6 basamağa sabitliyoruz: ondalıklı sayılar
 * bilgisayarda tam saklanamadığı için (0.1 + 0.2 = 0.30000000000000004)
 * gerçekte tam 19 olan bir toplam 18.999999999999996 olarak
 * hesaplanabilir ve kesme onu haksız yere 18'e düşürürdü.
 */
export function finalizeDailyDelta(grossDelta: number): number {
  const withoutFloatNoise = Number(grossDelta.toFixed(6));
  return Math.trunc(withoutFloatNoise);
}

/**
 * Değişimi Başarı Barı'na uygular ve sonucu 0–1000 aralığına sıkıştırır.
 * Bar tavana dayanmışsa daha fazla artmaz, tabana dayanmışsa daha fazla
 * düşmez (§7.2).
 */
export function applyDeltaToBar(currentBarValue: number, delta: number): number {
  const raw = currentBarValue + delta;
  return Math.min(SCORE_BAR_MAX, Math.max(SCORE_BAR_MIN, raw));
}

/* ==================================================================
   4) Gün içi canlı gösterim (§7.2)
   ================================================================== */

export interface LiveScore {
  /** Bugünün o ana kadarki ham (ondalıklı) toplamı. */
  grossDelta: number;
  /** Ekranda "+12" / "−5" olarak gösterilecek tam sayı değişim. */
  delta: number;
  /** Barda gösterilecek anlık değer (0–1000). */
  barValue: number;
}

/**
 * Kullanıcı bir alışkanlığı her işaretlediğinde çağrılır: bugünün o ana
 * kadarki değişimini ve barın anlık değerini hesaplar.
 *
 * `barValueAtDayStart`, `ProgressState.barValue` alanıdır — yani son
 * kapanmış günün sonundaki değer. Bugünün değişimi kalıcı kayda
 * yazılmaz, sadece gösterim için üstüne eklenir (§7.2).
 */
export function calculateLiveScore(
  dayLogs: HabitLog[],
  allHabits: Habit[],
  date: IsoDate,
  barValueAtDayStart: number,
): LiveScore {
  const grossDelta = calculateLiveGrossDelta(dayLogs, allHabits, date);
  const delta = finalizeDailyDelta(grossDelta);

  return {
    grossDelta,
    delta,
    barValue: applyDeltaToBar(barValueAtDayStart, delta),
  };
}

/* ==================================================================
   5) Day Streak uygunluğu (§7.3)
   ================================================================== */

export interface StreakEligibilityOptions extends DailyDeltaOptions {
  /** O gün aktif challenge tamamlandıysa `true`. */
  challengeCompletedToday: boolean;
}

/**
 * O günün Day Streak'e sayılıp sayılmayacağını söyler.
 *
 * Kural (§7.3): günün alışkanlıklarının **en az %50'si** yapılmış
 * olmalı. Parçalı alışkanlıklarda %50 ve üstü oran "yapıldı" sayılır
 * (0 ve 0.25 gibi bir değer zaten seçilemez, bkz. `CompletionRatio`).
 *
 * Challenge kuralı: tamamlanmış bir challenge günü, oranın yalnızca
 * "yapılan" tarafını 1 artırır; "toplam" tarafını DEĞİŞTİRMEZ. Yani
 * eşiği yükseltmeden günü kurtarmayı kolaylaştıran bir bonustur.
 *
 * Tatil günü kuralı (§7.7): tatil hakkı kullanılan gün, hiçbir şey
 * yapılmamış olsa bile streak'e otomatik sayılır.
 */
export function isDayStreakEligible(
  dayLogs: HabitLog[],
  allHabits: Habit[],
  options: StreakEligibilityOptions,
): boolean {
  // Dondurulmuş günün tüm amacı bu: o gün seri korunur — hakkın
  // tatilden mi ödülden mi geldiği burada fark etmez.
  if (options.isFrozenDay) {
    return true;
  }

  const habits = getScorableHabits(allHabits, options.date);
  const ratios = buildRatioMap(dayLogs);

  // §7.3: parçalı alışkanlıkta %50 ve üstü "yapıldı" sayılır.
  const doneCount = habits.filter(
    (habit) => (ratios.get(habit.id) ?? 0) >= 0.5,
  ).length;

  const effectiveDone = doneCount + (options.challengeCompletedToday ? 1 : 0);

  // Hiç alışkanlık tanımlanmamışsa (yeni kurulum) günü ancak
  // tamamlanmış bir challenge kurtarabilir; boş bir gün seri
  // başlatmamalı.
  if (habits.length === 0) {
    return options.challengeCompletedToday;
  }

  // "effectiveDone / habits.length >= 0.5" ile aynı şey — ama bölme
  // yapmadan. Ondalıklı bölmede oluşabilecek en küçük sapmanın bile
  // streak'i haksız yere koparmasını istemiyoruz.
  return effectiveDone * 2 >= habits.length;
}

/* ==================================================================
   6) Streak sayacı (§7.3)
   ================================================================== */

export interface StreakCounters {
  currentStreak: number;
  longestStreak: number;
}

/**
 * Gün kapanışında streak sayacını günceller.
 *
 * Gün uygunsa seri 1 artar, değilse **sıfırlanır** (§7.3 — seri
 * "kesintisiz" olmak zorunda). En uzun seri rekoru yalnızca yukarı
 * gider; sıfırlama onu etkilemez.
 */
export function applyStreakResult(
  counters: StreakCounters,
  isEligible: boolean,
): StreakCounters {
  const currentStreak = isEligible ? counters.currentStreak + 1 : 0;

  return {
    currentStreak,
    longestStreak: Math.max(counters.longestStreak, currentStreak),
  };
}
